/**
 * Ultimate Dashboard - Mission Control for Total Command
 * The dashboard users LOVE - complete control, clarity, and power
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { trpc } from '../lib/trpc'
import { useAuthStore } from '../stores/auth-store'
import { useDashboardCommandStore } from '../stores/dashboard-command-store'
import { useUIStore } from '../stores/ui-store'
import Layout from '../components/project/Layout'

// Dashboard Components
import FocusStrip from '../components/dashboard/FocusStrip'
import CommandMatrix from '../components/dashboard/CommandMatrix'
import IntelligenceFeed from '../components/dashboard/IntelligenceFeed'
import VelocityDashboard from '../components/dashboard/VelocityDashboard'
import TeamRadar from '../components/dashboard/TeamRadar'
import CommandBar from '../components/dashboard/CommandBar'

// UI Components
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { cn } from '../lib/utils'
import { 
  Zap, Target, Brain, Users, Activity, Command,
  LayoutDashboard, Minimize2, Maximize2, RefreshCw,
  Settings, HelpCircle, Loader2
} from 'lucide-react'

export default function UltimateDashboard() {
  const { workspaceId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { openTaskCreationModal } = useUIStore()
  
  // Dashboard store
  const {
    currentView,
    focusMode,
    commandBarOpen,
    focusPriority,
    commandMatrix,
    insights,
    velocityMetrics,
    teamRadar,
    statistics,
    userStatus,
    setView,
    toggleFocusMode,
    toggleCommandBar,
    setFocusPriority,
    updateCommandMatrix,
    updateVelocityMetrics,
    updateTeamRadar,
    updateStatistics,
    updateUserStatus,
    addInsight,
    handleRealtimeUpdate
  } = useDashboardCommandStore()
  
  const [lastRefresh, setLastRefresh] = useState(new Date())
  const [autoRefresh, setAutoRefresh] = useState(true)
  
  // Fetch dashboard data
  const { data: dashboardData, isLoading: dataLoading, refetch } = trpc.dashboardCommand.getCommandView.useQuery(
    { workspaceId: workspaceId! },
    {
      enabled: !!workspaceId,
      refetchInterval: autoRefresh ? 30000 : false, // Auto-refresh every 30s
    }
  )
  
  // Update store when data changes (memoized to prevent infinite loops)
  useEffect(() => {
    if (!dashboardData) return
    
    // Update store with fresh data
    if (dashboardData.focusPriority) setFocusPriority(dashboardData.focusPriority)
    updateCommandMatrix(dashboardData.commandMatrix || { now: [], today: [], tomorrow: [] })
    updateStatistics(dashboardData.statistics || { critical: 0, urgent: 0, totalToday: 0, streak: 0 })
    if (dashboardData.velocityMetrics) updateVelocityMetrics(dashboardData.velocityMetrics)
    updateTeamRadar(dashboardData.teamRadar || [])
    if (dashboardData.userStatus) updateUserStatus(dashboardData.userStatus)
    
    // Add insights (prevent duplicates)
    if (dashboardData.insights && Array.isArray(dashboardData.insights)) {
      dashboardData.insights.forEach(insight => {
        // Only add if not already in store
        addInsight(insight)
      })
    }
    
    setLastRefresh(new Date())
  }, [dashboardData?.focusPriority?.id, dashboardData?.statistics?.totalToday]) // Use specific fields to prevent re-renders
  
  // Get intelligence insights
  const { data: intelligence } = trpc.dashboardCommand.getIntelligence.useQuery(
    { workspaceId: workspaceId! },
    { 
      enabled: !!workspaceId,
      refetchInterval: 60000 // Refresh every minute
    }
  )
  
  // Suggest next action mutation
  const suggestNextMutation = trpc.dashboardCommand.suggestNextAction.useMutation({
    onSuccess: (result) => {
      if (result.taskId) {
        // Refresh dashboard data
        refetch()
      }
    }
  })
  
  // Update pulse mutation
  const updatePulseMutation = trpc.dashboardCommand.updatePulse.useMutation()
  
  // WebSocket subscription for real-time updates
  useEffect(() => {
    if (!workspaceId) return
    
    let ws: WebSocket | null = null
    
    try {
      // Subscribe to dashboard updates
      ws = new WebSocket(`ws://localhost:8000/ws/dashboard/${workspaceId}`)
      
      ws.onmessage = (event) => {
        try {
          const update = JSON.parse(event.data)
          handleRealtimeUpdate(update)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
    }
    
    return () => {
      if (ws) {
        ws.close()
      }
    }
  }, [workspaceId, handleRealtimeUpdate])
  
  // Auto-refresh handler
  useEffect(() => {
    if (!autoRefresh) return
    
    const interval = setInterval(() => {
      refetch()
    }, 30000)
    
    return () => clearInterval(interval)
  }, [autoRefresh, refetch])
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for command bar
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        toggleCommandBar()
      }
      
      // Cmd/Ctrl + / for help
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault()
        // Show help modal
      }
      
      // F for focus mode
      if (e.key === 'f' && !e.metaKey && !e.ctrlKey) {
        const target = e.target as HTMLElement
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault()
          toggleFocusMode()
        }
      }
    }
    
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [toggleCommandBar, toggleFocusMode])
  
  // Handle "I'm on it" action
  const handleStartFocus = useCallback(async () => {
    if (!focusPriority) return
    
    // Start the suggested task
    await suggestNextMutation.mutateAsync({ workspaceId: workspaceId! })
    
    // Enter focus mode
    toggleFocusMode()
    
    // Update user status to busy
    await updatePulseMutation.mutateAsync({
      workspaceId: workspaceId!,
      status: 'busy',
      currentFocus: focusPriority.id,
      availableToHelp: false
    })
  }, [focusPriority, workspaceId, suggestNextMutation, updatePulseMutation, toggleFocusMode])
  
  // Manual refresh
  const handleRefresh = useCallback(() => {
    refetch()
    setLastRefresh(new Date())
  }, [refetch])
  
  // Loading state
  if (dataLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-pink-500" />
            <p className="text-lg font-medium text-gray-700">Initializing Mission Control...</p>
            <p className="text-sm text-gray-500 mt-2">Analyzing patterns • Loading intelligence • Preparing dashboard</p>
          </div>
        </div>
      </Layout>
    )
  }
  
  return (
    <Layout>
      <div className={cn(
        "min-h-screen bg-white",
        focusMode && "focus-mode"
      )}>
        {/* Header Controls */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-light text-gray-900 flex items-center gap-3">
                <Zap className="w-7 h-7 text-pink-400" />
                Mission Control
              </h1>
              <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200 px-3 py-1">
                {currentView === 'command' ? 'Command View' : currentView === 'classic' ? 'Classic View' : 'Minimal View'}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              {/* View Switcher */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={currentView === 'command' ? 'default' : 'ghost'}
                  onClick={() => setView('command')}
                  className="h-8 px-4 text-sm font-medium"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Command View
                </Button>
                <Button
                  size="sm"
                  variant={currentView === 'classic' ? 'default' : 'ghost'}
                  onClick={() => setView('classic')}
                  className="h-8 px-4 text-sm font-medium"
                >
                  Classic
                </Button>
                <Button
                  size="sm"
                  variant={currentView === 'minimal' ? 'default' : 'ghost'}
                  onClick={() => setView('minimal')}
                  className="h-8 px-4 text-sm font-medium"
                >
                  Minimal
                </Button>
              </div>
              
              {/* Focus Mode Toggle */}
              <Button
                size="sm"
                variant={focusMode ? 'default' : 'outline'}
                onClick={toggleFocusMode}
                className={cn(
                  "h-9 px-4 border-gray-300",
                  focusMode 
                    ? "bg-pink-500 hover:bg-pink-600 border-pink-500 text-white" 
                    : "hover:bg-pink-50 hover:border-pink-300"
                )}
              >
                {focusMode ? <Minimize2 className="w-4 h-4 mr-2" /> : <Maximize2 className="w-4 h-4 mr-2" />}
                {focusMode ? 'Exit Focus' : 'Focus Mode'}
              </Button>
              
              {/* Refresh */}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRefresh}
                className="h-9 px-3 hover:bg-gray-100"
              >
                <RefreshCw className={cn(
                  "w-4 h-4",
                  autoRefresh && "animate-spin duration-3000"
                )} />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Focus Strip - The #1 Priority */}
        <FocusStrip
          priority={focusPriority}
          statistics={statistics}
          onStartFocus={handleStartFocus}
          focusMode={focusMode}
        />
        
        {/* Main Dashboard Content */}
        <div className="flex-1 flex gap-8 px-8 py-6">
          {/* Primary Content Area */}
          <div className="flex-1 space-y-8">
            {/* Command Matrix - Simplified */}
            <CommandMatrix
              matrix={commandMatrix}
              onTaskClick={(taskId) => navigate(`/task/${taskId}`)}
              onTaskDrop={(taskId, from, to) => {
                // Handle task movement
              }}
              simplified
            />
          </div>
          
          {/* Supporting Information Sidebar */}
          <div className="w-80 space-y-6">
            {/* Intelligence Insights */}
            <IntelligenceFeed
              insights={insights}
              intelligence={intelligence}
              onActionClick={(insight) => {
                if (insight.actionUrl) {
                  navigate(insight.actionUrl)
                }
              }}
              sidebar
            />
            
            {/* Team Status */}
            <TeamRadar
              members={teamRadar}
              userStatus={userStatus}
              onMemberClick={(memberId) => navigate(`/contacts?user=${memberId}`)}
              onOfferHelp={(memberId) => {
                navigate(`/chat?user=${memberId}`)
              }}
              compact
            />
            
            {/* Quick Metrics */}
            <VelocityDashboard
              metrics={velocityMetrics}
              compact
            />
          </div>
        </div>
        
        {/* Command Bar Overlay */}
        {commandBarOpen && (
          <CommandBar
            onClose={toggleCommandBar}
            onExecute={async (command) => {
              // Execute command
              const result = await trpc.dashboardCommand.executeCommand.mutate({
                workspaceId: workspaceId!,
                command
              })
              
              // Handle result
              console.log('Command result:', result)
            }}
          />
        )}
        
        {/* Focus Mode Overlay */}
        {focusMode && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-krushr-primary/5 to-krushr-primary/10" />
            <div className="absolute top-4 right-4 pointer-events-auto">
              <Badge className="bg-krushr-primary text-white px-3 py-1">
                <Target className="w-4 h-4 mr-1" />
                Focus Mode Active
              </Badge>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

// CSS for focus mode and grid layouts
const styles = `
  .focus-mode {
    animation: focus-pulse 4s ease-in-out infinite;
  }
  
  @keyframes focus-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.98; }
  }
  
  .dashboard-grid-command {
    animation: grid-enter 0.5s ease-out;
  }
  
  @keyframes grid-enter {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-spin-slow {
    animation: spin 3s linear infinite;
  }
`